import { useState, useEffect } from "react";
import AddUserModal from "./AddUserModal";
import { API_URL } from "../../config/api";
import { useOutletContext } from "react-router-dom";

const robotoStyle = `@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');* { font-family: 'Roboto', sans-serif !important; }`;

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];

// ─────────────────────────────────────────────
// Confirmation Modal Component
// ─────────────────────────────────────────────
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmLabel, confirmColor, loading }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => !loading && onClose()}
      />
      {/* Modal Card */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
        {/* Icon */}
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: `${confirmColor}18` }}
        >
          <svg
            className="w-6 h-6"
            style={{ color: confirmColor }}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>

        <h3 className="text-base font-semibold text-zinc-800 text-center mb-1">
          {title}
        </h3>
        <p className="text-sm text-zinc-500 text-center mb-6 leading-relaxed">
          {message}
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-zinc-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            No, Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: confirmColor }}
          >
            {loading && (
              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main UserList Component
// ─────────────────────────────────────────────
export default function UserList() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null,   // "deactivate" | "reactivate" | "delete"
    userId: null,
    userName: "",
  });
  const [actionLoading, setActionLoading] = useState(false);

  const { userData } = useOutletContext();
  const organizationId =
    userData?.user?.organization_links?.[0]?.organization?.organization_id;

  const ACCENT = "#4AACC5";

  const showToast = (message, type) => {
    console[type === "error" ? "error" : "log"](message);
  };

  // ── Debounce search input by 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        showToast("Authentication required. Please log in again.", "error");
        setLoading(false);
        return;
      }

      setUsers([]);
      setLoading(true);

      try {
        const params = new URLSearchParams();
        if (debouncedSearch) {
          const isEmail = debouncedSearch.includes("@");
          if (isEmail) {
            params.append("email", debouncedSearch);
          } else {
            params.append("name", debouncedSearch);
          }
        }

        const res = await fetch(`${API_URL}/user/get_all_users?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const json = await res.json();
        if (json.success) {
          setUsers(json.data);
        } else {
          showToast(json.message || "Failed to fetch users.", "error");
          setUsers([]);
        }
      } catch (err) {
        showToast("Something went wrong. Please try again.", "error");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [debouncedSearch]);

  const filtered = users;
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage,
  );

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // ── Status badge click → Deactivate or Reactivate confirmation
  const handleStatusClick = (user) => {
    if (user.is_active) {
      setConfirmModal({
        isOpen: true,
        type: "deactivate",
        userId: user.user_id,
        userName: user.full_name || "this user",
      });
    } else {
      setConfirmModal({
        isOpen: true,
        type: "reactivate",
        userId: user.user_id,
        userName: user.full_name || "this user",
      });
    }
  };

  // ── Delete button click → Permanent delete confirmation
  const handleDeleteClick = (user) => {
    setConfirmModal({
      isOpen: true,
      type: "delete",
      userId: user.user_id,
      userName: user.full_name || "this user",
    });
  };

  // ── Confirm action handler
  const handleConfirmAction = async () => {
    const token = localStorage.getItem("access_token");
    if (!token || !confirmModal.userId) return;

    setActionLoading(true);

    try {
      let res;

      if (confirmModal.type === "deactivate" || confirmModal.type === "reactivate") {
        // PATCH /user/{id}/status → toggle active/inactive
        res = await fetch(`${API_URL}/user/${confirmModal.userId}/status`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      } else {
        // DELETE /user/{id}?soft_delete=false → permanent delete
        res = await fetch(
          `${API_URL}/user/${confirmModal.userId}?soft_delete=false`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      const json = await res.json();

      if (json.success) {
        if (confirmModal.type === "delete") {
          // Hard delete → row remove பண்ணு
          setUsers((prev) =>
            prev.filter((u) => u.user_id !== confirmModal.userId)
          );
        } else {
          // Toggle status locally
          setUsers((prev) =>
            prev.map((u) =>
              u.user_id === confirmModal.userId
                ? { ...u, is_active: !u.is_active }
                : u
            )
          );
        }
        setConfirmModal({ isOpen: false, type: null, userId: null, userName: "" });
      } else {
        alert(json.message || "Action failed. Please try again.");
      }
    } catch (err) {
      alert("Something went wrong. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const closeConfirmModal = () => {
    if (!actionLoading) {
      setConfirmModal({ isOpen: false, type: null, userId: null, userName: "" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getUserRole = (roles) => {
    if (!roles || roles.length === 0) return "-";
    return roles[0]?.role?.name
      ? roles[0].role.name
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "-";
  };

  // ── Modal config based on type
  const modalConfig = {
    deactivate: {
      title: "Deactivate User?",
      message: `Are you sure you want to deactivate "${confirmModal.userName}"? They will lose access immediately.`,
      confirmLabel: "Yes, Deactivate",
      confirmColor: "#f59e0b",
    },
    reactivate: {
      title: "Reactivate User?",
      message: `Are you sure you want to reactivate "${confirmModal.userName}"? They will regain access immediately.`,
      confirmLabel: "Yes, Reactivate",
      confirmColor: "#22c55e",
    },
    delete: {
      title: "Permanently Delete User?",
      message: `Are you sure you want to permanently delete "${confirmModal.userName}"? This action cannot be undone.`,
      confirmLabel: "Yes, Delete",
      confirmColor: "#ef4444",
    },
  };

  const currentModal = modalConfig[confirmModal.type] || {};

  return (
    <div
      className="min-h-screen bg-gray-50 p-6"
      style={{ fontFamily: "'Roboto', sans-serif" }}
    >
      <style>{robotoStyle}</style>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        organizationId={organizationId}
      />

      {/* Deactivate / Reactivate / Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmAction}
        loading={actionLoading}
        title={currentModal.title}
        message={currentModal.message}
        confirmLabel={currentModal.confirmLabel}
        confirmColor={currentModal.confirmColor}
      />

      <h1 className="text-2xl font-bold text-zinc-800 mb-5 text-left">
        User List
      </h1>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="relative w-72">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-stone-300 focus:outline-none focus:ring-2 bg-white text-zinc-700 placeholder-stone-300"
          />
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 text-white text-xs font-semibold px-5 py-2.5 rounded-md transition-opacity hover:opacity-90"
          style={{ backgroundColor: ACCENT }}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add New User
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-md border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-sky-100 text-gray-700 font-semibold text-sm border-b border-zinc-200">
              <th className="px-4 py-3 text-left">User Name</th>
              <th className="px-4 py-3 text-left">User Type</th>
              <th className="px-4 py-3 text-left">User ID</th>
              <th className="px-4 py-3 text-left">Created Date</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-zinc-400 text-sm">
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin w-4 h-4"
                      style={{ color: ACCENT }}
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Loading...
                  </div>
                </td>
              </tr>
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-zinc-400 text-sm">
                  No users found.
                </td>
              </tr>
            ) : (
              paginated.map((user) => (
                <tr
                  key={user.user_id}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                >
                  {/* User Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.full_name}
                          className="w-9 h-9 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                          style={{ backgroundColor: ACCENT }}
                        >
                          {user.full_name?.charAt(0).toUpperCase() || "?"}
                        </div>
                      )}
                      <span className="text-zinc-800 font-normal">
                        {user.full_name || "-"}
                      </span>
                    </div>
                  </td>

                  {/* User Type */}
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-2xl text-xs font-medium text-white"
                      style={{ backgroundColor: ACCENT }}
                    >
                      {getUserRole(user.roles)}
                    </span>
                  </td>

                  {/* User ID */}
                  <td className="px-4 py-3 text-zinc-800">{user.user_id ?? "-"}</td>

                  {/* Created Date */}
                  <td className="px-4 py-3 text-zinc-800">{formatDate(user.created_at)}</td>

                  {/* Email */}
                  <td className="px-4 py-3 text-zinc-800">{user.email || "-"}</td>

                  {/* Status — click to toggle active/inactive */}
                  <td className="px-4 py-3 text-center">
                    {user.is_active ? (
                      <span
                        onClick={() => handleStatusClick(user)}
                        title="Click to deactivate"
                        className="inline-flex items-center px-2.5 py-0.5 rounded-2xl bg-green-100 text-lime-800 text-xs cursor-pointer hover:bg-green-200 transition-colors select-none"
                      >
                        Active
                      </span>
                    ) : (
                      <span
                        onClick={() => handleStatusClick(user)}
                        title="Click to reactivate"
                        className="inline-flex items-center px-2.5 py-0.5 rounded-2xl bg-rose-50 text-red-600 text-xs cursor-pointer hover:bg-red-100 transition-colors select-none"
                      >
                        Inactive
                      </span>
                    )}
                  </td>

                  {/* Actions — permanent delete button */}
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleDeleteClick(user)}
                      className="hover:opacity-70 transition-opacity mx-auto"
                      title="Permanently Delete"
                      style={{ color: "#ef4444" }}
                    >
                      <svg
                        className="w-4 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M9 6V4h6v2" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={safePage === 1}
            className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-slate-100 disabled:opacity-40 hover:bg-slate-50 transition-colors"
          >
            <svg
              className="w-3.5 h-3.5 text-neutral-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className="w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-normal transition-colors"
              style={
                safePage === page
                  ? { backgroundColor: ACCENT, borderColor: ACCENT, color: "white" }
                  : { backgroundColor: "white", borderColor: "#e2e8f0", color: "#27272a" }
              }
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-slate-100 disabled:opacity-40 hover:bg-slate-50 transition-colors"
          >
            <svg
              className="w-3.5 h-3.5 text-neutral-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-3 text-sm text-gray-700">
          <span>Results per page</span>
          <select
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            className="h-8 px-2 bg-slate-100 rounded-lg text-zinc-700 font-medium text-sm border-none outline-none cursor-pointer"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-zinc-500">{filtered.length} total</span>
        </div>
      </div>
    </div>
  );
}